import React, { useState, useRef } from "react";
import { api, mediaUrl, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ImagePlus, X, Loader2, Type, Film, Star, BarChart3, Radio, ThumbsUp, Plus, Users } from "lucide-react";
import { EmojiButton, FeelingButton } from "./EmojiFeeling";
import { LinkPreview } from "./LinkPreview";
import { GifPicker } from "./GifPicker";
import { FEELINGS } from "../lib/api";
import { toast } from "sonner";

const TYPES = [
  { key: "text", label: "Text", icon: Type },
  { key: "text_media", label: "Text + Media", icon: ImagePlus },
  { key: "media", label: "Media only", icon: Film },
  { key: "recommendation", label: "Recommend", icon: ThumbsUp },
  { key: "poll", label: "Poll", icon: BarChart3 },
  { key: "live", label: "Live", icon: Radio },
];

export function PostComposer({ onCreated, spotId, alert = false }) {
  const { user } = useAuth();
  const [type, setType] = useState("text");
  const [text, setText] = useState("");
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [rec, setRec] = useState({ title: "", category: "", rating: 5 });
  const [pollQ, setPollQ] = useState("");
  const [pollOpts, setPollOpts] = useState(["", ""]);
  const [liveUrl, setLiveUrl] = useState("");
  const [tagged, setTagged] = useState([]);
  const [tagQuery, setTagQuery] = useState("");
  const [tagResults, setTagResults] = useState([]);
  const [feeling, setFeeling] = useState(null);
  const [debouncedText, setDebouncedText] = useState("");
  const fileRef = useRef();

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedText(text), 600);
    return () => clearTimeout(t);
  }, [text]);

  const reset = () => { setText(""); setMedia([]); setRec({ title: "", category: "", rating: 5 }); setPollQ(""); setPollOpts(["", ""]); setLiveUrl(""); setTagged([]); setTagQuery(""); setTagResults([]); setFeeling(null); };

  React.useEffect(() => {
    if (!tagQuery.trim()) { setTagResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/users/search?q=${encodeURIComponent(tagQuery)}`); setTagResults(data); } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [tagQuery]);

  const pickMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = 10 - media.length;
    if (room <= 0) { toast.error("You can add up to 10 photos/videos."); return; }
    const batch = files.slice(0, room);
    setUploading(true);
    try {
      for (const file of batch) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMedia((prev) => [...prev, { url: data.url, type: file.type.startsWith("video") ? "video" : "image" }]);
      }
      if (files.length > room) toast.info(`Added ${room} — max is 10.`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const tagAllPride = async () => {
    try {
      const { data } = await api.get("/friends");
      if (!data.length) { toast.info("You have no Pride to tag yet."); return; }
      setTagged(data.map((u) => ({ id: u.id, name: u.name, username: u.username })));
      toast.success(`Tagged all ${data.length} of your Pride`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const submit = async () => {
    setPosting(true);
    try {
      const payload = { type, text };
      if (type === "text_media" || type === "media") payload.media = media;
      if (type === "recommendation") payload.recommendation = rec;
      if (type === "poll") payload.poll = { question: pollQ, options: pollOpts };
      if (type === "live") payload.live_url = liveUrl;
      if (feeling) payload.feeling = feeling;
      payload.tagged_user_ids = tagged.map((t) => t.id);
      if (spotId) payload.spot_id = spotId;
      if (alert) payload.is_alert = true;
      const { data } = await api.post("/posts", payload);
      reset(); setType("text");
      onCreated?.(data);
      toast.success(alert ? "Alert posted!" : "Posted!");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  const showText = type !== "media";

  return (
    <div className="leopard-post mb-6" data-testid={alert ? "alert-composer" : "post-composer"}>
      <div className="leopard-post-inner p-4">
        {/* type selector */}
        {!alert && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {TYPES.map((t) => {
            const Icon = t.icon;
            const active = type === t.key;
            return (
              <button key={t.key} data-testid={`composer-type-${t.key}`} onClick={() => setType(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-black text-xs font-heading font-bold transition-colors ${active ? "bg-spotter-maroon text-spotter-cream" : "bg-white hover:bg-spotter-cream"}`}>
                <Icon className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>
        )}

        <div className="flex gap-3">
          <Avatar user={user} size="md" />
          <div className="flex-1 space-y-3">
            {showText && (
              <Textarea data-testid="composer-text" value={text} onChange={(e) => setText(e.target.value)}
                placeholder={alert ? "Post an alert for everyone on The Prowl…" : type === "recommendation" ? "Why do you recommend it?" : type === "poll" ? "Say something about your poll (optional)…" : type === "live" ? "Tell everyone what you're streaming…" : "What's popping Spotter?"}
                className="border-2 border-black resize-none min-h-[64px] text-base focus-visible:ring-spotter-gold" />
            )}

            {showText && (
              <div className="flex flex-wrap items-center gap-2" data-testid="composer-feeling-row">
                <EmojiButton onPick={(e) => setText((t) => t + e)} testid="composer-emoji-btn" />
                <FeelingButton value={feeling} onPick={setFeeling} testid="composer-feeling-btn" />
              </div>
            )}

            {showText && debouncedText && <LinkPreview text={debouncedText} className="max-w-md" />}

            {(type === "text_media" || type === "media") && (
              <div>
                <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={pickMedia} data-testid="composer-file-input" />
                {media.length > 0 && (
                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mb-2" data-testid="composer-media-preview">
                    {media.map((m, i) => (
                      <div key={i} className="relative aspect-square rounded-lg border-2 border-black overflow-hidden bg-spotter-cream">
                        {m.type === "video"
                          ? <video src={mediaUrl(m.url)} className="w-full h-full object-cover" />
                          : <img src={mediaUrl(m.url)} alt="" className="w-full h-full object-cover" />}
                        <button data-testid={`composer-remove-media-${i}`} onClick={() => setMedia(media.filter((_, j) => j !== i))}
                          className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {media.length < 10 && (
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" data-testid="composer-add-media" onClick={() => fileRef.current?.click()} disabled={uploading}
                      className="border-2 border-black gap-2 bg-white hover:bg-spotter-cream">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4 text-spotter-gold" />} Add photos or videos {media.length > 0 && `(${media.length}/10)`}
                    </Button>
                    <GifPicker onSelect={(url) => setMedia((prev) => prev.length < 10 ? [...prev, { url, type: "image" }] : prev)}
                      trigger={<Button type="button" variant="outline" data-testid="composer-add-gif" className="border-2 border-black gap-2 bg-white hover:bg-spotter-cream font-heading font-bold text-spotter-maroon">GIF</Button>} />
                  </div>
                )}
              </div>
            )}

            {type === "recommendation" && (
              <div className="space-y-2 bg-spotter-cream border-2 border-black rounded-lg p-3">
                <Input data-testid="rec-title" value={rec.title} onChange={(e) => setRec({ ...rec, title: e.target.value })} placeholder="What are you recommending? (movie, song, place…)" className="border-2 border-black" />
                <Input data-testid="rec-category" value={rec.category} onChange={(e) => setRec({ ...rec, category: e.target.value })} placeholder="Category (optional)" className="border-2 border-black" />
                <div className="flex items-center gap-1">
                  <span className="text-sm font-semibold mr-1">Rating:</span>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} data-testid={`rec-star-${n}`} onClick={() => setRec({ ...rec, rating: n })}>
                      <Star className="w-6 h-6" style={{ color: "#E19C00", fill: n <= rec.rating ? "#E19C00" : "transparent" }} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {type === "poll" && (
              <div className="space-y-2 bg-spotter-cream border-2 border-black rounded-lg p-3">
                <Input data-testid="poll-question" value={pollQ} onChange={(e) => setPollQ(e.target.value)} placeholder="Ask a question…" className="border-2 border-black" />
                {pollOpts.map((o, i) => (
                  <div key={i} className="flex gap-2">
                    <Input data-testid={`poll-option-${i}`} value={o} onChange={(e) => setPollOpts(pollOpts.map((x, j) => j === i ? e.target.value : x))} placeholder={`Option ${i + 1}`} className="border-2 border-black" />
                    {pollOpts.length > 2 && <button onClick={() => setPollOpts(pollOpts.filter((_, j) => j !== i))} className="px-2 border-2 border-black rounded-md"><X className="w-4 h-4" /></button>}
                  </div>
                ))}
                {pollOpts.length < 6 && <Button type="button" variant="outline" data-testid="poll-add-option" onClick={() => setPollOpts([...pollOpts, ""])} className="border-2 border-black gap-1 bg-white"><Plus className="w-4 h-4" />Add option</Button>}
              </div>
            )}

            {type === "live" && (
              <div className="space-y-1 bg-spotter-cream border-2 border-black rounded-lg p-3">
                <Input data-testid="live-url" value={liveUrl} onChange={(e) => setLiveUrl(e.target.value)} placeholder="Paste your live stream link (YouTube, Twitch, etc.)" className="border-2 border-black" />
                <p className="text-xs text-spotter-maroon flex items-center gap-1"><Radio className="w-3 h-3" />Your stream will appear with a LIVE badge on the feed.</p>
              </div>
            )}

            <div className="relative">
              {tagged.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="composer-tags">
                  {tagged.map((t) => (
                    <span key={t.id} className="inline-flex items-center gap-1 bg-spotter-gold/30 border-2 border-black rounded-full px-2 py-0.5 text-xs font-bold">
                      @{t.username}
                      <button onClick={() => setTagged(tagged.filter((x) => x.id !== t.id))}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <Input data-testid="composer-tag-input" value={tagQuery} onChange={(e) => setTagQuery(e.target.value)} placeholder="Tag people (your Pride or anyone)…" className="border-2 border-black h-9" />
              <button type="button" data-testid="composer-tag-all-pride" onClick={tagAllPride} className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-spotter-maroon underline">
                <Users className="w-3.5 h-3.5" />Tag all my Pride
              </button>
              {tagResults.length > 0 && (
                <div className="absolute z-30 mt-1 w-full bg-white border-2 border-black rounded-lg overflow-hidden brand-shadow-sm">
                  {tagResults.slice(0, 6).map((u) => (
                    <button key={u.id} data-testid={`tag-result-${u.id}`} onClick={() => { if (!tagged.find((x) => x.id === u.id)) setTagged([...tagged, u]); setTagQuery(""); setTagResults([]); }}
                      className="w-full flex items-center gap-2 p-2 hover:bg-spotter-cream text-left">
                      <Avatar user={u} size="sm" /><span className="text-sm font-semibold">{u.name}</span><span className="text-xs text-spotter-maroon">@{u.username}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button data-testid="composer-submit" onClick={submit} disabled={posting || uploading}
                className="bg-spotter-maroon text-spotter-cream font-heading font-bold border-2 border-black brand-shadow-sm">
                {posting ? "Posting…" : type === "live" ? "Go Live" : "Post"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
